import type {
  SearchFilterCondition,
  SearchFilterConditionCustomAttribute,
  SearchFilterConditionDate,
  SearchFilterField,
  SearchFilterOperator,
  SearchFilters,
} from 'lib-search-types';

const nullishOperators = ['is_empty', 'is_not_empty'] as const;

export const dateFilterFields = [
  'abandoned_at',
  'assigned_at',
  'closed_at',
  'created_at',
  'last_message_created_at',
  'note_created_at',
  'updated_at',
] as const;

export type SearchFilterConditionDateField = (typeof dateFilterFields)[number];

/**
 * Checks if the given filter node is a leaf filter condition (has field,
 * operator, values).
 */
export function isLeafFilterCondition(
  node: SearchFilterCondition<'conversations'> | SearchFilters<'conversations'>,
): node is SearchFilterCondition<'conversations'> {
  return 'field' in node;
}

/**
 * Checks if the given filter node is a nested filter group (has strategy and
 * filters).
 */
export function isNestedFilterGroup(
  node: SearchFilterCondition<'conversations'> | SearchFilters<'conversations'>,
): node is SearchFilters<'conversations'> {
  return 'strategy' in node && 'filters' in node;
}

/** Checks if the given operator is nullish */
export function isNullishFilterOperator(
  operator: SearchFilterOperator<'conversations'>,
): operator is (typeof nullishOperators)[number] {
  return nullishOperators.includes(
    operator as (typeof nullishOperators)[number],
  );
}

/** Checks if the given condition is a date condition */
export function isDateFilterCondition(
  condition: SearchFilterCondition<'conversations'>,
): condition is SearchFilterConditionDate<SearchFilterConditionDateField> {
  return isDateFilterField(condition.field);
}

/** Checks if the given condition field is a date field. */
export function isDateFilterField(
  field: SearchFilterField<'conversations'>,
): field is SearchFilterConditionDateField {
  return dateFilterFields.includes(field as SearchFilterConditionDateField);
}

/** Checks if the given condition is a custom attribute condition */
export function isCustomAttributeCondition(
  condition: SearchFilterCondition<'conversations'>,
): condition is
  | SearchFilterConditionCustomAttribute<'conversation_custom_attribute'>
  | SearchFilterConditionCustomAttribute<'contact_custom_attribute'> {
  return isCustomAttributeField(condition.field);
}

/** Checks if the given condition field is a custom attribute field. */
export function isCustomAttributeField(
  field: SearchFilterField<'conversations'>,
): field is 'conversation_custom_attribute' | 'contact_custom_attribute' {
  return (
    field === 'conversation_custom_attribute' ||
    field === 'contact_custom_attribute'
  );
}
